import React, { useEffect, useMemo, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import api from "../../api";
import { useAuth } from "../../AuthContext";

const HUB_URL = `${process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "")}/hubs/support`;

const ChatForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [hasUnread, setHasUnread] = useState(false);

  const connectionRef = useRef(null);
  const joinedConversationRef = useRef(null);
  const isOpenRef = useRef(false);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const previousMessageCountRef = useRef(0);

  const { isAuthenticated } = useAuth();

  const guestMessage = useMemo(() => ({
    id: "guest-message",
    senderRole: "admin",
    message: "Vous devez être connecté pour envoyer un message au support.",
    isRead: true,
  }), []);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // ---------- SignalR ----------

  useEffect(() => {
    if (!isAuthenticated) {
      connectionRef.current?.stop();
      connectionRef.current = null;
      joinedConversationRef.current = null;
      return;
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("MessageReceived", async (newMessage) => {
      updateConversationMessages(newMessage);

      if (newMessage.senderRole === "admin") {
        if (isOpenRef.current) {
          await markAsRead();
          requestAnimationFrame(() => scrollToBottom("smooth"));
        } else {
          setHasUnread(true);
        }
      }
    });

    connection.start().catch((err) => {
      console.error("SignalR connection error:", err);
    });

    connectionRef.current = connection;

    return () => {
      connection.stop();
      connectionRef.current = null;
      joinedConversationRef.current = null;
    };
  }, [isAuthenticated]);

  // Rejoindre le groupe conversation dès qu'on a un conversationId
  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || !conversation?.id) return;
    if (joinedConversationRef.current === conversation.id) return;

    const join = async () => {
      try {
        if (connection.state !== signalR.HubConnectionState.Connected) {
          await new Promise((resolve) => {
            const interval = setInterval(() => {
              if (connection.state === signalR.HubConnectionState.Connected) {
                clearInterval(interval);
                resolve();
              }
            }, 100);
          });
        }
        await connection.invoke("JoinConversation", conversation.id);
        joinedConversationRef.current = conversation.id;
      } catch (err) {
        console.error("JoinConversation error:", err);
      }
    };

    join();
  }, [conversation?.id]);

  // ---------- Scroll ----------

  const scrollToBottom = (behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
  };

  const isNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < 100;
  };

  useEffect(() => {
    const currentCount = messages.length;
    if (currentCount > previousMessageCountRef.current && isNearBottom()) {
      scrollToBottom("smooth");
    }
    previousMessageCountRef.current = currentCount;
  }, [messages]);

  // ---------- Unread ----------

  useEffect(() => {
    const sourceMessages = conversation?.messages || messages || [];
    if (!sourceMessages.length) { setHasUnread(false); return; }

    const hasUnreadAdmin = sourceMessages.some(
      (msg) => msg.senderRole === "admin" && !msg.isRead
    );
    setHasUnread(hasUnreadAdmin && !isOpen);
  }, [conversation?.messages, messages, isOpen]);

  // ---------- Helpers state ----------

  const updateConversationMessages = (newMessage) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === newMessage.id)) return prev;
      return [...prev, newMessage];
    });

    setConversation((prev) => {
      if (!prev) return {
        id: newMessage.conversationId,
        status: "open",
        lastMessageAt: newMessage.createdAt ?? null,
        messages: [newMessage],
      };

      const exists = (prev.messages || []).some((m) => m.id === newMessage.id);
      return {
        ...prev,
        status: "open",
        lastMessageAt: newMessage.createdAt ?? prev.lastMessageAt,
        messages: exists ? prev.messages : [...(prev.messages || []), newMessage],
      };
    });
  };

  const markAdminMessagesAsReadLocally = () => {
    setMessages((prev) =>
      prev.map((msg) => msg.senderRole === "admin" ? { ...msg, isRead: true } : msg)
    );
    setConversation((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: (prev.messages || []).map((msg) =>
          msg.senderRole === "admin" ? { ...msg, isRead: true } : msg
        ),
      };
    });
    setHasUnread(false);
  };

  // ---------- API ----------

  const markAsRead = async () => {
    if (!isAuthenticated) return;
    try {
      await api.post("/support/conversation/read");
      markAdminMessagesAsReadLocally();
    } catch (err) {
      console.error("Erreur markAsRead:", err);
    }
  };

  const fetchConversation = async (silent = false) => {
    if (!isAuthenticated) {
      setConversation(null);
      setMessages([guestMessage]);
      setHasUnread(false);
      return null;
    }

    try {
      if (!silent) { setLoading(true); setError(""); }

      const res = await api.get("/support/conversation");
      const incoming = res.data.conversation;

      if (!incoming) {
        setConversation(null);
        setMessages([]);
        setHasUnread(false);
        return null;
      }

      setConversation(incoming);
      setMessages(incoming.messages || []);
      return incoming;
    } catch (err) {
      if (!silent) setError("Impossible de charger la conversation support.");
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setConversation(null);
      setMessages([guestMessage]);
      setHasUnread(false);
      return;
    }
    fetchConversation(true);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isOpen) fetchConversation(false);
  }, [isOpen]);

  const sendMessage = async () => {
    if (!isAuthenticated || !input.trim()) return;
    try {
      setSending(true);
      setError("");

      const res = await api.post("/support/messages", { message: input.trim() });
      const newMessage = res.data.message;

      updateConversationMessages(newMessage);
      setInput("");

      if (!conversation?.id) await fetchConversation(true);

      requestAnimationFrame(() => scrollToBottom("smooth"));
    } catch (err) {
      setError("Impossible d'envoyer votre message.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleToggleOpen = async () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);

    if (nextOpen && isAuthenticated) {
      const loaded = await fetchConversation(true);
      const hasUnreadAdmin = loaded?.messages?.some(
        (msg) => msg.senderRole === "admin" && !msg.isRead
      );
      if (hasUnreadAdmin) await markAsRead();
      requestAnimationFrame(() => scrollToBottom("auto"));
    }
  };

  const isClosed = conversation?.status === "closed";

  // ---------- Render ----------

  return (
    <>
      <button
        onClick={handleToggleOpen}
        className="fixed bottom-5 right-5 z-[9999] inline-flex items-center gap-3 rounded-full bg-cyan-600 px-5 py-3 font-semibold text-white shadow-xl transition hover:bg-cyan-500"
      >
        <span className="relative inline-flex items-center justify-center">
          <span className="text-base">💬</span>
          {hasUnread && !isOpen && (
            <span className="absolute -right-2 -top-2 h-3.5 w-3.5 rounded-full border-2 border-slate-950 bg-red-500" />
          )}
        </span>
        <span>Chat</span>
      </button>

      {isOpen && (
        <div className="fixed bottom-20 right-5 z-[9999] flex h-[580px] w-[390px] flex-col overflow-hidden rounded-3xl border border-slate-700 bg-slate-900 text-white shadow-2xl">
          <div className="border-b border-slate-700 bg-slate-900 px-5 py-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-base font-semibold text-white">Chat admin</p>
                <p className="text-xs text-slate-400">
                  {isAuthenticated ? "Échangez en direct avec l'administration" : "Connexion requise"}
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-xl px-2.5 py-1.5 text-slate-300 transition hover:bg-slate-800 hover:text-white"
              >
                ✕
              </button>
            </div>
          </div>

          <div
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto bg-slate-950/80 px-4 py-4"
          >
            {loading ? (
              <div className="rounded-2xl border border-slate-800 bg-slate-900/70 px-4 py-3 text-sm text-slate-300">
                Chargement...
              </div>
            ) : error ? (
              <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            ) : messages.length === 0 ? (
              <div className="rounded-2xl border border-slate-800 bg-slate-900/70 px-4 py-3 text-sm text-slate-400">
                Aucun message pour le moment.
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg) => {
                  const isAdminMessage = msg.senderRole === "admin";
                  return (
                    <div key={msg.id} className={`flex ${isAdminMessage ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[85%] rounded-2xl px-3.5 py-3 text-sm leading-6 shadow-sm ${
                        isAdminMessage
                          ? "border border-slate-700 bg-slate-800 text-slate-100"
                          : "bg-cyan-600 text-white"
                      }`}>
                        <div className="mb-1 flex items-center gap-2">
                          <span className="text-[11px] font-semibold opacity-80">
                            {isAdminMessage ? "Administration" : "Vous"}
                          </span>
                          {isAdminMessage && !msg.isRead && (
                            <span className="rounded-full bg-cyan-500/20 px-2 py-0.5 text-[10px] font-semibold text-cyan-300">
                              Nouveau
                            </span>
                          )}
                        </div>
                        <p className="whitespace-pre-wrap break-words">{msg.message}</p>
                        {!isAdminMessage && (
                          <div className="mt-1 text-right text-[11px] text-cyan-100/80">
                            {msg.isRead ? "Lu par le support" : "Envoyé"}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <div className="border-t border-slate-700 bg-slate-900 px-4 py-4">
            {!isAuthenticated && (
              <div className="mb-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-300">
                Connectez-vous pour écrire au support.
              </div>
            )}
            {isClosed && (
              <div className="mb-3 rounded-2xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-300">
                Cette conversation est fermée. Un nouveau message créera un nouveau ticket si nécessaire.
              </div>
            )}
            <div className="flex flex-col gap-3">
              <textarea
                rows="3"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={!isAuthenticated || sending}
                placeholder={!isAuthenticated ? "Connexion requise pour écrire" : "Écrivez votre message..."}
                className="w-full resize-none rounded-2xl border border-slate-700 bg-slate-950 px-3.5 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-60"
              />
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-slate-500">
                  Entrée pour envoyer · Maj + Entrée pour aller à la ligne
                </p>
                <button
                  onClick={sendMessage}
                  disabled={!isAuthenticated || !input.trim() || sending}
                  className="rounded-2xl bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-cyan-500 disabled:cursor-not-allowed disabled:bg-slate-600"
                >
                  {sending ? "Envoi..." : "Envoyer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatForm;